/**
 * NepaliDateTimePicker - Main class for the Nepali DateTime Picker library
 * Updated with fixes for date calculation and day of week logic
 */

import DatePicker from "./pickers/DatePicker.js";
import DateTimePicker from "./pickers/DateTimePicker.js";
import DateRangePicker from "./pickers/DateRangePicker.js";
import DateTimeRangePicker from "./pickers/DateTimeRangePicker.js";
import NepaliDateConverter from "./converters/NepaliDateConverter.js";

class NepaliDateTimePicker {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      type: "date", // 'date', 'datetime', 'daterange', 'datetimerange'
      calendar: "nepali", // 'nepali' or 'english'
      language: "en", // 'en' or 'ne'
      format: null, // Date/time format (will use defaults based on type)
      theme: "default", // 'default', 'dark', or custom theme
      size: "md", // 'sm', 'md', 'lg' - controls calendar size
      closeOnSelect: true,
      showInput: true,
      inline: false,
      minDate: null,
      maxDate: null,
      disabledDates: [],
      disabledDaysOfWeek: [],
      ...options,
    };

    this.picker = null;
    this.converter = new NepaliDateConverter();
    this.isInputElement = false; // Flag to track if initialized on an input

    this._initialize();
  }

  /**
   * Initialize the picker based on type
   * @private
   */
  _initialize() {
    // Select the element if it's a string selector
    if (typeof this.element === "string") {
      this.element = document.querySelector(this.element);
    }

    if (!this.element) {
      throw new Error("Element not found");
    }

    // Check if element is an input
    this.isInputElement = this.element.tagName === "INPUT";

    // Set default formats based on type
    if (!this.options.format) {
      switch (this.options.type) {
        case "date":
          this.options.format = "YYYY-MM-DD";
          break;
        case "datetime":
          this.options.format = "YYYY-MM-DD HH:mm";
          break;
        case "daterange":
          this.options.format = "YYYY-MM-DD";
          break;
        case "datetimerange":
          this.options.format = "YYYY-MM-DD HH:mm";
          break;
      }
    }

    // Apply theme
    this._applyTheme();

    // Create appropriate picker
    switch (this.options.type) {
      case "date":
        this.picker = new DatePicker(this.options);
        break;
      case "datetime":
        this.picker = new DateTimePicker(this.options);
        break;
      case "daterange":
        this.picker = new DateRangePicker(this.options);
        break;
      case "datetimerange":
        this.picker = new DateTimeRangePicker(this.options);
        break;
      default:
        throw new Error(`Invalid picker type: ${this.options.type}`);
    }

    // Initialize picker
    this.picker.initialize(this.element);

    // For input elements, parse any existing value
    if (this.isInputElement && this.element.value) {
      this._parseInitialValue();
    }
  }

  /**
   * Parse initial value from input
   * @private
   */
  _parseInitialValue() {
    const value = this.element.value;
    if (!value) return;

    try {
      switch (this.options.type) {
        case "date":
          const date = this.parseDate(
            value,
            this.options.format,
            this.options.calendar
          );
          if (date) this.setValue(date);
          break;
        case "datetime":
          const parts = value.split(" ");
          if (parts.length === 2) {
            const datePart = this.parseDate(
              parts[0],
              this.options.dateFormat || this.options.format.split(" ")[0],
              this.options.calendar
            );
            const timeParts = parts[1].split(":");
            if (datePart && timeParts.length >= 2) {
              const hour = parseInt(timeParts[0], 10);
              const minute = parseInt(timeParts[1], 10);
              this.setValue({
                date: datePart,
                time: { hour, minute },
              });
            }
          }
          break;
        // Add support for daterange and datetimerange if needed
      }
    } catch (e) {
      console.warn("Failed to parse initial value:", e);
    }
  }

  /**
   * Apply theme styles
   * @private
   */
  _applyTheme() {
    const element = this.element;
    const themeClass = `ndtp-theme-${this.options.theme}`;

    // For input elements, we'll apply the theme to the wrapper
    // that will be created during initialization
    if (!this.isInputElement) {
      // Remove existing theme classes
      element.classList.remove("ndtp-theme-default", "ndtp-theme-dark");

      // Add new theme class
      if (this.options.theme) {
        element.classList.add(themeClass);
      }
    }
  }

  /**
   * Show the picker
   */
  show() {
    if (this.picker && this.picker.show) {
      this.picker.show();
    }
  }

  /**
   * Hide the picker
   */
  hide() {
    if (this.picker && this.picker.hide) {
      this.picker.hide();
    }
  }

  /**
   * Get selected value
   * @returns {Object|null} Selected value
   */
  getValue() {
    if (!this.picker) return null;

    switch (this.options.type) {
      case "date":
        // Check if getDate method exists, otherwise try getSelectedDate
        if (typeof this.picker.getDate === "function") {
          return this.picker.getDate();
        } else if (typeof this.picker.getSelectedDate === "function") {
          return this.picker.getSelectedDate();
        } else if (this.picker.selectedDate) {
          return this.picker.selectedDate;
        }
        return null;

      case "datetime":
        if (typeof this.picker.getDateTime === "function") {
          return this.picker.getDateTime();
        }
        return null;

      case "daterange":
      case "datetimerange":
        if (typeof this.picker.getRange === "function") {
          return this.picker.getRange();
        }
        return null;

      default:
        return null;
    }
  }

  /**
   * Set value
   * @param {Object|string} value - Value to set
   */
  setValue(value) {
    if (!this.picker) return;

    switch (this.options.type) {
      case "date":
        if (typeof this.picker.setDate === "function") {
          this.picker.setDate(value);
        } else if (typeof this.picker.setSelectedDate === "function") {
          this.picker.setSelectedDate(value);
        } else if (typeof this.picker.selectDate === "function") {
          this.picker.selectDate(value);
        }
        break;

      case "datetime":
        if (typeof this.picker.setDateTime === "function") {
          this.picker.setDateTime(value);
        }
        break;

      case "daterange":
      case "datetimerange":
        if (typeof this.picker.setRange === "function") {
          this.picker.setRange(value);
        }
        break;
    }
  }

  /**
   * Clear value
   */
  clear() {
    if (this.picker && this.picker.clear) {
      this.picker.clear();
    }
  }

  /**
   * Destroy the picker
   */
  destroy() {
    if (this.picker && this.picker.destroy) {
      this.picker.destroy();
    }
  }

  /**
   * Update configuration
   * @param {Object} options - New options
   */
  updateConfig(options) {
    this.options = { ...this.options, ...options };

    if (this.picker && this.picker.updateConfig) {
      this.picker.updateConfig(options);
    } else {
      // Reinitialize with new options
      const currentValue = this.getValue();
      this.destroy();
      this._initialize();
      if (currentValue) {
        this.setValue(currentValue);
      }
    }
  }

  /**
   * Convert date between calendars
   * @param {Object} date - Date to convert
   * @param {string} fromCalendar - Source calendar ('nepali' or 'english')
   * @param {string} toCalendar - Target calendar ('nepali' or 'english')
   * @returns {Object} Converted date
   */
  convertDate(date, fromCalendar, toCalendar) {
    if (fromCalendar === toCalendar) {
      return date;
    }

    if (fromCalendar === "nepali" && toCalendar === "english") {
      return this.converter.bsToAd(date);
    } else if (fromCalendar === "english" && toCalendar === "nepali") {
      return this.converter.adToBs(date);
    }

    throw new Error("Invalid calendar conversion");
  }

  /**
   * Format date
   * @param {Object} date - Date to format
   * @param {string} format - Format string
   * @param {string} calendar - Calendar type ('nepali' or 'english')
   * @returns {string} Formatted date
   */
  formatDate(date, format = "YYYY-MM-DD", calendar = "nepali") {
    if (calendar === "nepali") {
      return this.converter.formatBsDate(date, format);
    } else {
      return this.converter.formatAdDate(date, format);
    }
  }

  /**
   * Parse date string
   * @param {string} dateString - Date string to parse
   * @param {string} format - Format string
   * @param {string} calendar - Calendar type ('nepali' or 'english')
   * @returns {Object|null} Parsed date
   */
  parseDate(dateString, format = "YYYY-MM-DD", calendar = "nepali") {
    // This is a simple implementation - could be enhanced
    const parts = dateString.split(/[-/]/);
    if (parts.length !== 3) return null;

    let year, month, day;

    if (format.startsWith("YYYY")) {
      [year, month, day] = parts;
    } else if (format.startsWith("DD")) {
      [day, month, year] = parts;
    } else {
      [month, day, year] = parts;
    }

    return {
      year: parseInt(year, 10),
      month: parseInt(month, 10),
      day: parseInt(day, 10),
    };
  }

  /**
   * Get today's date
   * @param {string} calendar - Calendar type ('nepali' or 'english')
   * @returns {Object} Today's date
   */
  getToday(calendar = "nepali") {
    if (calendar === "nepali") {
      return this.converter.getTodayBS();
    } else {
      return this.converter.getTodayAD();
    }
  }

  /**
   * Apply theme and size styles
   * @private
   */
  _applyTheme() {
    const element = this.element;
    const themeClass = `ndtp-theme-${this.options.theme}`;
    const sizeClass = `ndtp-size-${this.options.size}`;

    // For input elements, we'll apply the theme to the wrapper
    // that will be created during initialization
    if (!this.isInputElement) {
      // Remove existing theme and size classes
      element.classList.remove(
        "ndtp-theme-default",
        "ndtp-theme-dark",
        "ndtp-size-sm",
        "ndtp-size-md",
        "ndtp-size-lg"
      );

      // Add new theme and size classes
      if (this.options.theme) {
        element.classList.add(themeClass);
      }

      if (this.options.size) {
        element.classList.add(sizeClass);
      }
    }
  }

  /**
   * Static method to create picker instances
   * @param {HTMLElement|string} element - Element or selector
   * @param {Object} options - Options
   * @returns {NepaliDateTimePicker} Picker instance
   */
  static create(element, options = {}) {
    return new NepaliDateTimePicker(element, options);
  }

  /**
   * Initialize all pickers on the page
   * @param {string} selector - CSS selector for picker elements
   * @param {Object} defaultOptions - Default options
   * @returns {Array} Array of picker instances
   */
  static init(selector = "[data-picker]", defaultOptions = {}) {
    const elements = document.querySelectorAll(selector);
    const instances = [];

    elements.forEach((element) => {
      const options = {
        ...defaultOptions,
        ...element.dataset,
      };

      // Convert data attributes to proper types
      if (options.inline === "true") options.inline = true;
      if (options.closeOnSelect === "false") options.closeOnSelect = false;
      if (options.showInput === "false") options.showInput = false;
      if (options.disabledDates)
        options.disabledDates = JSON.parse(options.disabledDates);
      if (options.disabledDaysOfWeek)
        options.disabledDaysOfWeek = JSON.parse(options.disabledDaysOfWeek);

      instances.push(new NepaliDateTimePicker(element, options));
    });

    return instances;
  }

  /**
   * Version info
   */
  static get version() {
    return "1.0.0";
  }

  /**
   * Export utilities
   */
  static get utils() {
    return {
      NepaliDateConverter,
    };
  }
}
// Export for different module systems
if (typeof module !== "undefined" && module.exports) {
  module.exports = NepaliDateTimePicker;
} else if (typeof define === "function" && define.amd) {
  define([], function () {
    return NepaliDateTimePicker;
  });
} else {
  window.NepaliDateTimePicker = NepaliDateTimePicker;
}

export default NepaliDateTimePicker;
